import { ErrorHandler, Injectable, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import posthog from 'posthog-js';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private router = inject(Router);
  private authService = inject(AuthService);
  private enabled = false;

  /** Inicializa o PostHog; sem `posthogKey` no environment fica desabilitado. */
  init(): void {
    if (!environment.posthogKey) return;

    posthog.init(environment.posthogKey, {
      api_host: environment.posthogHost,
      defaults: '2025-05-24',
      // Pageviews são enviados manualmente a cada NavigationEnd (SPA).
      capture_pageview: false,
      // Só cria perfil para quem foi identificado (usuários logados).
      person_profiles: 'identified_only',
      session_recording: { maskAllInputs: true },
    });
    posthog.register({ app_env: environment.appEnv });
    this.enabled = true;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => posthog.capture('$pageview'));

    // Identifica apenas por ID e perfil — sem nome nem e-mail (LGPD).
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
        posthog.identify(user.id_usuario, {
          id_perfil: user.id_perfil,
          id_empresa: user.id_empresa ?? null,
        });
      } else {
        posthog.reset();
      }
    });
  }

  captureError(error: unknown): void {
    if (!this.enabled) return;
    posthog.captureException(error);
  }
}

/** Repassa erros não tratados ao PostHog e mantém o log no console. */
@Injectable()
export class AnalyticsErrorHandler implements ErrorHandler {
  private analytics = inject(AnalyticsService);

  handleError(error: unknown): void {
    this.analytics.captureError(error);
    console.error(error);
  }
}
